import type { Role } from '../../../../core/policy/roles';

/**
 * Who on the committee may do what — owned by this feature, implemented in
 * `infrastructure/` (§3.4, §4.1).
 *
 * Roles are custom claims. The security rules read them from the verified
 * token, so a client-writable role would be a client-writable permission
 * (ADR-004): granting one runs in a Cloud Function, and this port only asks.
 */

export interface CommitteeMember {
  readonly authUid: string;
  readonly email: string;
  readonly employeeId: string;
  readonly fullName: string;
  /**
   * A copy kept on the account document.
   *
   * Custom claims cannot be listed or queried, so this mirror is what a screen
   * can show. The claim on the token remains what the rules enforce; the
   * function that changes one writes both.
   */
  readonly roles: readonly Role[];
}

export type RolesFailureCode = 'forbidden' | 'not_found' | 'invalid' | 'network' | 'unknown';

export class RolesError extends Error {
  readonly code: RolesFailureCode;

  constructor(code: RolesFailureCode, message: string) {
    super(message);
    this.name = 'RolesError';
    this.code = code;
  }
}

export interface RolesPort {
  /** Accounts already linked to an employee record. Bounded (§11.5.1). */
  listLinkedAccounts(): Promise<readonly CommitteeMember[]>;

  /** Replaces someone's roles. An empty list removes them all. */
  setRoles(targetUid: string, roles: readonly Role[]): Promise<void>;
}
